/**
 * 编辑机器人弹窗 - 参考 openlink.chat 设计风格
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { updateRobot } from '../api/robots';
import { toast } from 'sonner';

interface EditRobotModalProps {
  robot: {
    id: string;
    robot_name: string;
    robot_identifier?: string;
    session_limit: number;
    web_hook?: string;
  };
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const editRobotSchema = z.object({
  robot_name: z.string(),
  robot_identifier: z.string().optional(),
  session_limit: z.string().optional(),
  web_hook: z.string().url('Webhook 必须是有效的 URL').optional().or(z.literal('')),
});

type EditRobotForm = z.infer<typeof editRobotSchema>;

export default function EditRobotModal({ robot, isOpen, onClose, onSuccess }: EditRobotModalProps) {
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, reset } = useForm<EditRobotForm>({
    resolver: zodResolver(editRobotSchema),
  });

  useEffect(() => {
    if (isOpen && robot) {
      reset({
        robot_name: robot.robot_name,
        robot_identifier: robot.robot_identifier || '',
        session_limit: robot.session_limit === 0 ? '' : String(robot.session_limit),
        web_hook: robot.web_hook || '',
      });
    }
  }, [isOpen, robot, reset]);

  const onSubmit = async (data: EditRobotForm) => {
    setIsLoading(true);
    try {
      await updateRobot(robot.id, {
        robot_identifier: data.robot_identifier || undefined,
        session_limit: data.session_limit ? parseInt(data.session_limit) : 0,
        web_hook: data.web_hook || undefined,
      });
      toast.success('机器人信息已更新');
      onSuccess();
      onClose();
    } catch (error: any) {
      // 错误已在 axios 拦截器中处理
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">编辑机器人信息</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">全局机器人名称（@别名）</label>
            <input type="text" disabled className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600" {...register('robot_name')} />
            <p className="text-xs text-gray-500 mt-1">仅机器人持有者可凭 AK 修改全局名称；群主不能替他人改名。</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">机器人标识（可选）</label>
            <input type="text" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red" {...register('robot_identifier')} placeholder="例如：钉钉机器人 ID" />
            <p className="text-xs text-gray-500 mt-1">用于标识真实 IM 平台的机器人</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">会话限制（可选）</label>
            <input type="number" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red" {...register('session_limit')} placeholder="0 表示无限制" min="0" />
            <p className="text-xs text-gray-500 mt-1">0 表示无限制</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Webhook（可选）</label>
            <input type="url" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-lobster-red" {...register('web_hook')} placeholder="https://example.com/webhook" />
            <p className="text-xs text-gray-500 mt-1">用于接收事件推送</p>
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">取消</button>
            <button type="submit" disabled={isLoading} className="flex-1 px-4 py-2 text-white bg-lobster-red rounded-lg hover:bg-lobster-red/90 disabled:opacity-50">
              {isLoading ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
